/**
 * contextualRecallEngine.js
 *
 * Filters what memories are safe and useful to surface in a prompt,
 * and which ones should remain silent.
 *
 * Rule: recall mínimo útil.
 * Surface only what helps the CURRENT pastoral interaction.
 * Omit anything that would feel like surveillance, artificial, or irrelevant.
 *
 * Recall is filtered by:
 *   — recallMode (from continuitySensitivityEngine)
 *   — temporal relevance (how recent the memory is)
 *   — emotional safety (not surfacing shame when person is in crisis)
 *   — pastoral utility (does surfacing this help right now?)
 *   — privacy/implicit sensitivity (don't cite exact dates or counts)
 */

import { buildMilestonesInstruction } from "./spiritualMilestoneTracker";

/**
 * @param {{
 *   recallMode:       "explicit"|"implicit"|"silent",
 *   recallDepth:      "full"|"soft"|"none",
 *   arc:              string,
 *   temporalContext:  string|null,           — from longitudinalDiscernmentEngine
 *   pattern:          string,
 *   patternDescription: string|null,
 *   milestones:       import("./spiritualMilestoneTracker").Milestone[],
 *   regressionInstruction: string|null,
 *   milestoneInstruction:  string|null,
 *   sensitivityInstruction: string|null,
 *   processState:     string,
 *   emotion:          object|null,
 * }} params
 * @returns {{
 *   recallLines:  string[],   — lines to include in [CONTINUIDAD PASTORAL] block
 *   recallActive: boolean,
 * }}
 */
export function filterContextualRecall({
  recallMode,
  recallDepth,
  arc,
  temporalContext,
  pattern,
  patternDescription,
  milestones,
  regressionInstruction,
  milestoneInstruction,
  sensitivityInstruction,
  processState,
  emotion,
}) {
  if (recallMode === "silent" || recallDepth === "none") {
    return { recallLines: [], recallActive: false };
  }

  const dom   = emotion?.dominantEmotion ?? "none";
  const lines = [];

  // 1. Sensitivity instruction (always first if present — frames everything)
  if (sensitivityInstruction) {
    lines.push(sensitivityInstruction);
  }

  // 2. Temporal context — only if recallDepth allows and arc adds real depth
  const SILENT_ARCS = new Set(["sin_historia", "estabilidad_consistente", "apertura_nueva"]);
  if (temporalContext && !SILENT_ARCS.has(arc)) {
    // Don't surface temporal context when person is currently in active quebranto
    // and we're in short conversation (they haven't spoken yet)
    if (!(processState === "quebranto" && recallMode === "implicit")) {
      lines.push(temporalContext);
    }
  }

  // 3. Regression/restoration instruction — always useful, compact
  if (regressionInstruction) {
    lines.push(regressionInstruction);
  }

  // 4. Pattern description — only in full depth, and only for non-trivial patterns
  const TRIVIAL_PATTERNS = new Set(["sin_patron", "estabilidad_consistente"]);
  if (
    recallDepth === "full" &&
    patternDescription &&
    !TRIVIAL_PATTERNS.has(pattern) &&
    recallMode === "explicit"
  ) {
    lines.push(patternDescription);
  }

  // 5. Milestone instruction — only explicit mode, only if emotionally appropriate
  // Don't surface milestones when person is in active shame/grief (feels performative)
  if (
    milestoneInstruction &&
    recallMode === "explicit" &&
    dom !== "grief" &&
    dom !== "shame"
  ) {
    lines.push(milestoneInstruction);
  }

  return {
    recallLines: lines.filter(Boolean),
    recallActive: lines.length > 0,
  };
}
